import { trackActionsService } from '@ctct/domain-layer';
import type { UpdateBillingInfoPayload } from '@ctct/state-server-layer';

import { ValidationError } from '../errors';
import { type TEventSource, controllerHelpers, type Controller, type Listeners } from '../helpers';
import {
  type RouterNonUiManager,
  updateBillingEventBus,
  type AlertManager,
  type BillingInfoFormManager,
  type PaymentMethodsManager,
  type UpdateBillingManager,
} from '../managers';

export class UpdateMoretonBayBillingController implements Controller {
  public listeners: Listeners;

  constructor(
    private paymentMethods: PaymentMethodsManager,
    private billingInfoForm: BillingInfoFormManager,
    private updateBilling: UpdateBillingManager,
    private alert: AlertManager,
    private router: RouterNonUiManager
  ) {
    this.listeners = controllerHelpers.createListeners([
      updateBillingEventBus.bindTo('requestUpdateInfo', this.updateInfo),
    ]);
  }

  private updateInfo = async ({ source }: { source: TEventSource }) => {
    if (source === 'api') {
      // for moreton bay, we don't want to update the info (attmept to submit the form) if it's called from the api (which is the case when user selects paypal as payment method).

      return;
    }

    const [paymentMethods, billingInfoForm, updateBilling, alertApi] = await Promise.all([
      this.paymentMethods.getApiWhenReady(),
      this.billingInfoForm.getApiWhenReady(),
      this.updateBilling.getApiWhenReady(),
      this.alert?.getApiWhenReady(),
    ]);

    if (!(billingInfoForm.isFormDirty() || paymentMethods.isNewPaymentMethod())) {
      return;
    }

    // reset error and set loading to true
    updateBilling.reportBeginLoading();

    // set widgets to loading state
    billingInfoForm.setIsLoading(true);
    paymentMethods.setTabsLoading(true);

    // remove existing alerts
    alertApi?.removeAllAlerts();

    try {
      // validate the form if it was changed or required fields are not filled
      if (billingInfoForm.isFormDirty() || !billingInfoForm.allRequiredFieldsAreFilledWithDefaultValues()) {
        const isFormValid = await billingInfoForm.triggerValidation();

        if (!isFormValid) {
          trackActionsService.billingInfoUpgradeFormValidationFailed();
          throw new ValidationError('Form validation failed');
        }

        billingInfoForm.clearBillingFormData();
      }

      // create a payload for API call with customer info
      const customerInfo = billingInfoForm.getValuesForApiRequest();
      const payload: UpdateBillingInfoPayload = {
        customer_info: customerInfo,
      };

      // if new payment method is selected - get its payload and add it to the payload
      if (paymentMethods.isNewPaymentMethod()) {
        try {
          payload.payment_info = await paymentMethods.getPayloadForUpdateBillingInfoApi({
            postalCode: customerInfo.postal_code4
              ? `${customerInfo.postal_code}-${customerInfo.postal_code4}`
              : customerInfo.postal_code,
            countryCode: customerInfo.country,
            fullName: `${customerInfo.first_name} ${customerInfo.last_name}`,
            email: customerInfo.email,
            phone: customerInfo.phone,
          });
        } catch (error) {
          trackActionsService.billingInfoUpgradePaymentMethodFailed();
          throw error;
        }
      }

      // call API
      await updateBilling.submitBillingChanges(payload);

      updateBilling.reportSuccess();

      const routerApi = await this.router.getApiWhenReady();

      routerApi.goToMoretonBayConfirmation(payload.customer_info.first_name);
    } catch (error) {
      // Ignore validation errors here, since they are reported directly on the form fields.
      if (error instanceof ValidationError) {
        updateBilling.reportEndLoading();
      } else if (error instanceof Error && error.message) {
        updateBilling.reportError(error);

        alertApi?.addAlert({
          message: error.message,
          id: error.message,
        });
      } else {
        const newError = new Error(`Unknown error: ${String(error)}`);

        updateBilling.reportError(newError);

        alertApi?.addAlert({
          message: newError.message,
          id: newError.message,
        });
      }
    } finally {
      billingInfoForm.setIsLoading(false);
      paymentMethods.setTabsLoading(false);
    }
  };
}
